<template>
  <div class="reset-page">
    <UiContainer class="reset-page__container">
      <div class="reset-page__card-wrap">
        <UiCard class="reset-page__card">
          <h1 class="reset-page__title">Новый пароль</h1>
          <template v-if="!token">
            <p class="reset-page__error" role="alert">Ссылка для сброса пароля некорректна.</p>
            <UiButton to="/forgot-password" block>Запросить новую ссылку</UiButton>
          </template>
          <form v-else class="reset-page__form" @submit.prevent="submit">
            <div class="reset-page__field">
              <label class="reset-page__label" for="reset-password">Новый пароль</label>
              <UiInput
                id="reset-password"
                v-model="password"
                type="password"
                autocomplete="new-password"
                minlength="8"
                required
              />
              <span class="reset-page__help">Не менее 8 символов</span>
            </div>
            <div class="reset-page__field">
              <label class="reset-page__label" for="reset-password-confirm">
                Повторите пароль
              </label>
              <UiInput
                id="reset-password-confirm"
                v-model="passwordConfirm"
                type="password"
                autocomplete="new-password"
                minlength="8"
                required
              />
            </div>
            <p v-if="errorMessage" class="reset-page__error" role="alert">{{ errorMessage }}</p>
            <UiButton type="submit" block :loading="loading">Сохранить пароль</UiButton>
            <router-link
              v-if="linkExpired"
              to="/forgot-password"
              class="reset-page__link reset-page__link--center"
            >
              Запросить новую ссылку
            </router-link>
          </form>
        </UiCard>
      </div>
    </UiContainer>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthSession } from '@/modules/auth/public'
import UiButton from '@/shared/ui/UiButton.vue'
import UiCard from '@/shared/ui/UiCard.vue'
import UiContainer from '@/shared/ui/UiContainer.vue'
import UiInput from '@/shared/ui/UiInput.vue'

const MIN_PASSWORD_LENGTH = 8

const route = useRoute()
const router = useRouter()
const authStore = useAuthSession()

const token = computed(() => {
  const value = route.query['token']
  return typeof value === 'string' ? value.trim() : ''
})
const password = ref('')
const passwordConfirm = ref('')
const loading = ref(false)
const errorMessage = ref('')
const linkExpired = ref(false)

async function submit() {
  errorMessage.value = ''
  linkExpired.value = false

  if (password.value.length < MIN_PASSWORD_LENGTH) {
    errorMessage.value = `Пароль должен содержать не менее ${MIN_PASSWORD_LENGTH} символов`
    return
  }
  if (password.value !== passwordConfirm.value) {
    errorMessage.value = 'Пароли не совпадают'
    return
  }

  loading.value = true
  try {
    const result = await authStore.resetPassword(token.value, password.value)
    if (result.success) {
      await router.replace({ name: 'login', query: { passwordReset: '1' } })
      return
    }
    errorMessage.value = result.error || 'Не удалось изменить пароль'
    linkExpired.value = /ссылк/i.test(errorMessage.value)
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.reset-page {
  min-height: calc(100vh - 160px);
  display: flex;
  flex-direction: column;
}

.reset-page__container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 32px;
  padding-bottom: 32px;
}

.reset-page__card-wrap {
  width: 100%;
  max-width: 400px;
}

.reset-page__card {
  padding: 24px;
}

.reset-page__title {
  margin: 0 0 24px;
  font-size: 24px;
  font-weight: 600;
  text-align: center;
}

.reset-page__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.reset-page__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.reset-page__label {
  font-size: 14px;
  font-weight: 500;
  color: #334155;
}

.reset-page__help {
  font-size: 12px;
  color: #586578;
}

.reset-page__error {
  margin: 0;
  font-size: 14px;
  color: #b91c1c;
}

.reset-page__link {
  color: #0f172a;
  font-weight: 500;
  text-decoration: none;
  font-size: 14px;
}

.reset-page__link--center {
  text-align: center;
}

.reset-page__link:hover {
  text-decoration: underline;
}
</style>
